<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import FormViewer from '@components/admin/form-viewer'
import { fetchWithdraw, createTransfer } from '@apis/admin/withdraw'
import fromPairs from 'lodash/fromPairs'
import TransferList from '@components/admin/withdraw-transfer-list'

export default {
  components: { FormViewer, TransferList },
  props: {
    withdraw_seq: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: false,
      info: {},
      transfer_params: {},
      field_values: [],
      forms: [],
      active_tab: 'info',
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      Promise.all([this.loadInfo()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadInfo() {
      this.loading = true
      const { withdraw_info, field_values, forms } = await fetchWithdraw(
        this.withdraw_seq
      )

      this.info = withdraw_info
      this.field_values = field_values
      this.forms = forms
    },
    async handleCreateTransfer() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('withdraw_info.msg_re_transfer'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await createTransfer({
          withdraw_seq: this.withdraw_seq,
        })

        this.$notify({
          title: this.$t('withdraw_info.notification'),
          message: this.$t('withdraw_info.msg_registered'),
          type: 'success',
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    async handleCreateTransferKeyin() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('withdraw_info.msg_key_in'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        await createTransfer({
          ...this.transfer_params,
          withdraw_seq: this.withdraw_seq,
          is_keyin: 1,
        })

        this.$notify({
          title: this.$t('withdraw_info.notification'),
          message: this.$t('withdraw_info.msg_information'),
          type: 'success',
        })

        this.transfer_params = {}
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <div v-loading="loading">
    <el-tabs v-model="active_tab" :stretch="true">
      <el-tab-pane label="$t('withdraw_info.application_details')" name="info">
        <h3 :class="$style.xeAdmin">{{ $t('admin.default_info') }}</h3>
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="150" />
            <col width="*" />
            <col width="150" />
            <col width="*" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.kyc_cdd_name_id') }}</div></th
              >
              <td>{{ info.user_name }} ({{ info.user_id }})</td>
              <th scope="row"
                ><div>{{ $t('admin.email_address') }}</div></th
              >
              <td>{{ info.email_address }}</td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.asset_title') }}</div></th
              >
              <td>{{ info.asset_title }}</td>
              <th scope="row"
                ><div>{{ $t('wallet.account_number') }}</div></th
              >
              <td>{{ info.account_number }}</td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.modal_asset_code') }}</div></th
              >
              <td>{{ info.asset_code }}</td>
              <th scope="row"
                ><div>{{ $t('admin.request_amount') }}</div></th
              >
              <td>{{ info.request_amount }}</td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('admin.status') }}</div></th
              >
              <td>{{ fromPairs(constants.deposit.status)[info.status] }}</td>
              <th scope="row"
                ><div>{{ $t('admin.regdate') }}</div></th
              >
              <td>{{ date('Y-m-d H:i:s', new Date(info.regdate)) }}</td>
            </tr>
            <tr v-if="info.withdraw_address_certification_seq">
              <th scope="row"
                ><div>{{ $t('admin.withdraw_certification') }}</div></th
              >
              <td colspan="3">
                <template v-if="info.bank_code">
                  {{ $t('admin.bank') }}:
                  {{
                    fromPairs(
                      constants.kyc.asset_withdraw_address_certification
                        .bank_codes
                    )[info.bank_code]
                  }},
                </template>
                <template v-if="info.holder_name">
                  {{ $t('admin.holder_name') }}: {{ info.holder_name }},
                </template>
                {{ $t('admin.withdraw_address') }}: {{ info.withdraw_address }}

                <a
                  href="#"
                  :class="[$style.button, $style.red, $style.fr]"
                  @click.prevent="handleCreateTransfer"
                  ><span>{{ $t('withdraw_info.re_transfer') }}</span></a
                >
              </td>
            </tr>
          </tbody>
        </table>
        <h3 :class="$style.xeAdmin">{{ $t('withdraw_info.key_in') }}</h3>
        <form @submit.prevent="handleCreateTransferKeyin">
          <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
            <colgroup>
              <col width="150" />
              <col width="*" />
              <col width="150" />
              <col width="*" />
            </colgroup>
            <tbody>
              <tr>
                <th scope="row"
                  ><div>{{ $t('withdraw_info.amount_transferred') }}</div></th
                >
                <td
                  ><input
                    v-model="transfer_params.amount"
                    type="text"
                    :class="[$style.inputTypeText, $style.tRight, $style.w80]"
                /></td>
                <th scope="row"
                  ><div>{{ $t('withdraw_info.transfer_fee') }}</div></th
                >
                <td
                  ><input
                    v-model="transfer_params.fee_amount"
                    type="text"
                    :class="[$style.inputTypeText, $style.tRight, $style.w80]"
                /></td>
              </tr>
              <tr>
                <th scope="row"
                  ><div>{{ $t('withdraw_info.send_address') }}</div></th
                >
                <td colspan="3"
                  ><input
                    v-model="transfer_params.from"
                    type="text"
                    :class="[$style.inputTypeText, $style.fullWidth]"
                /></td>
              </tr>
              <tr>
                <th scope="row"
                  ><div>{{ $t('withdraw_info.incoming_address') }}</div></th
                >
                <td colspan="3"
                  ><input
                    v-model="transfer_params.to"
                    type="text"
                    :class="[$style.inputTypeText, $style.fullWidth]"
                /></td>
              </tr>
              <tr>
                <th scope="row"
                  ><div>{{ 'TXID' }}</div></th
                >
                <td colspan="3"
                  ><input
                    v-model="transfer_params.txid"
                    type="text"
                    :class="[$style.inputTypeText, $style.fullWidth]"
                /></td>
              </tr>
              <tr>
                <th scope="row"
                  ><div>{{ $t('withdraw_info.message') }}</div></th
                >
                <td colspan="3"
                  ><input
                    v-model="transfer_params.message"
                    type="text"
                    :class="[$style.inputTypeText, $style.fullWidth]"
                /></td>
              </tr>
              <tr>
                <th colspan="4" :class="[$style.buttonArea]">
                  <span :class="[$style.button, $style.black, $style.fr]"
                    ><input
                      type="submit"
                      :value="$t('withdraw_info.submission')"
                  /></span>
                </th>
              </tr>
            </tbody>
          </table>
        </form>
        <FormViewer v-bind="{ field_values, forms }"></FormViewer>
      </el-tab-pane>
      <el-tab-pane
        :label="$t('withdraw_info.transfer_history')"
        name="transfer"
      >
        <TransferList :withdraw_seq="withdraw_seq"></TransferList>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
